import { configureStore, isPlain } from "@reduxjs/toolkit";
import { connectRouter, routerMiddleware } from "connected-react-router";
import { createBrowserHistory } from "history";
import reduceReducers from "reduce-reducers";
import { combineReducers, compose } from "redux";
import { USER_FOUND } from "redux-oidc";
import { entitiesReducer, queryMiddleware, actionTypes, errorsReducer } from "redux-query";
import { administrationReducers } from "./administration";
import { authReducers } from "./auth";
import { benchmarkCreationReducers } from "./benchmark-creation";
import { crossSliceReducer } from "./cross-slice";
import { currentDashboardReducers } from "./current-dashboard";
import { customErrorsReducers } from "./custom-errors";
import { customQueriesStateReducers } from "./custom-queries";
import { datepickerReducers } from "./datepicker";
import { editJsonWidgetReducers } from "./edit-json-widget";
import { mainGridSettingsReducers } from "./grid-settings";
import { gridsReducers } from "./grids";
import { inspectDataReducers } from "./inspectdata";
import { leftPaneReducers } from "./left-pane";
import { messagesDialogReducers } from "./messages-dialog";
import { groupDatapointsMiddleware } from "./middleware/analytics/analytics-grouping-middleware";
import { modalsReducers } from "./modal";
import { notificationsReducers } from "./notification";
import { otherModulesDataReducers } from "./otherModules";
import { portfolioCreationReducers } from "./portfolio-creation";
import { pdfPreferencesReducers } from "./pdf-preferences";
import { asyncRequestsReducers } from "./signalR";
import superAgentNetworkInterface from "./superAgentNetworkInterface";
import { TagReducers } from "./tags";
import { designerReducers, getInitialSections, getInitialTemplate, addWidgetReducer } from "./template";
import { tenantSettingsReducers } from "./tenant";
import { themeReducers } from "./themes";
import { widgetWorkshopReducer } from "./widget-workshop";
import { widgetsWithoutDataReducers } from "./widgets-without-data";
import { busMiddleware } from "common/ins-message-bus";
import { tagsApiMiddleware, tagsApiReducers } from "components/tags/tagsApi";
import { commitmentPacingMiddleware, commitmentPacingReducers, commitmentPacingReducersPath } from "components/commitment-pacing/store/api";
import { getStateValue } from "plugins/get-state";
import { authMiddleware } from "plugins/oidc";
import { websocketMiddleware } from "plugins/websocket";
import { documentListReducers } from "components/clickthrough/store";
import { documentLibraryMiddleware, documentLibraryReducers, documentLibraryReducersPath } from "components/clickthrough/store/api";
import { noteModuleName, notesApiMiddleware, notesApiReducer, notesApiReducerPath } from "./notes/api";
import { noteReducer } from "./notes";
import { settingsReducers } from "./settings";
import { userPreferencesApiMiddleware, userPreferencesApiReducer, userPreferencesApiReducerPath } from "./settings/api";
import { apiMiddleware as workspacesApiMiddleware, apiReducers as workspacesApiReducers, apiReducersPath as workspacesApiReducersPath } from "components/workspaces/store/api";
export * from "./themes";
export * from "./custom-queries";
export const initialEntitiesState = {
    AdminMarket: {},
    AdminMarkets: {},
    AllFundSeries: [],
    Analytics: {},
    Asset: {},
    Assets: {},
    CashflowCustomModel: {},
    CashflowCustomModelVersions: {},
    CashflowCustomModels: {},
    CashflowFile: {},
    Comments: {},
    Datapoints: {},
    DatapointsAllowedDimensions: {},
    DatapointsFilters: {},
    DatapointsMetadata: {},
    EmptyTemplate: {},
    ExportDataExcelHistories: {},
    Fund: {},
    FundDefaultModel: {},
    FundModelAssociations: {},
    FundSeries: {},
    Funds: {},
    FundsAndCffModels: {},
    FundsPage: {},
    GenericPmis: {},
    Image: {},
    ImportDataDetailedLogs: {},
    ImportDataHistories: {},
    ImportDataSummaryLogs: {},
    ImportDataTemplates: {},
    InspectData: {},
    Manager: {},
    Managers: {},
    Market: {},
    Markets: {},
    ModelAssociation: {},
    ObjectiveAndCffModels: {},
    PmiAssociations: {},
    PmiFile: {},
    PmiPrices: {},
    Pmis: {},
    Portfolio: {},
    PortfolioComparisonAssociation: {},
    PortfolioCreation: {},
    Portfolios: {},
    PredefinedTAModels: {},
    PrivateBenchmark: {},
    PrivateBenchmarks: {},
    PrivateBenchmarksWithReferenceDate: {},
    Section: getInitialSections(),
    SectionWithWidgets: {},
    TACashflows: {},
    TAModels: {},
    Template: getInitialTemplate(),
    TenantPrivateBenchmark: {},
    UserGroups: {},
    UserSettings: {},
    Userprefs: {},
    Widget: {}
};
const preloadedState = {
    entities: initialEntitiesState
};
const staticReducers = (hist) => ({
    addWidget: addWidgetReducer,
    administration: administrationReducers,
    asyncRequests: asyncRequestsReducers,
    auth: authReducers,
    benchmarkCreation: benchmarkCreationReducers,
    [commitmentPacingReducersPath]: commitmentPacingReducers,
    createTag: TagReducers,
    currentDashboard: currentDashboardReducers,
    customQueries: customQueriesStateReducers,
    datepicker: datepickerReducers,
    designer: designerReducers,
    [documentLibraryReducersPath]: documentLibraryReducers,
    documentList: documentListReducers,
    editJsonWidget: editJsonWidgetReducers,
    entities: entitiesReducer,
    errors: customErrorsReducers,
    grids: gridsReducers,
    inspectData: inspectDataReducers,
    leftPane: leftPaneReducers,
    mainGridSettings: mainGridSettingsReducers,
    messagesDialog: messagesDialogReducers,
    modals: modalsReducers,
    [noteModuleName]: noteReducer,
    [notesApiReducerPath]: notesApiReducer,
    notifications: notificationsReducers,
    otherModulesData: otherModulesDataReducers,
    pdfPreferences: pdfPreferencesReducers,
    portfolioCreation: portfolioCreationReducers,
    requestErrors: errorsReducer,
    router: connectRouter(hist),
    settings: settingsReducers,
    tags: tagsApiReducers,
    tenantState: tenantSettingsReducers,
    theme: themeReducers,
    [userPreferencesApiReducerPath]: userPreferencesApiReducer,
    widgetsWithoutData: widgetsWithoutDataReducers,
    workshop: widgetWorkshopReducer,
    [workspacesApiReducersPath]: workspacesApiReducers
});
export const history = createBrowserHistory();
const createReducer = (injectedReducers = {}) => {
    const combined = combineReducers({
        ...staticReducers(history),
        ...injectedReducers
    });
    return reduceReducers(combined, crossSliceReducer);
};
export const getQueries = (state) => state.customQueries;
export const getEntities = (state) => state.entities;
const injectedMiddlwares = [];
const createInjectorsEnhancer = (next) => (reducer, initial) => ({
    ...next(reducer, initial),
    addMiddleware: (middleware) => injectedMiddlwares.pushIfNotExist({ middleware }),
    createReducer,
    injectedReducers: {}
});
const middlewares = [
    commitmentPacingMiddleware,
    authMiddleware,
    groupDatapointsMiddleware,
    busMiddleware,
    websocketMiddleware,
    tagsApiMiddleware,
    documentLibraryMiddleware,
    notesApiMiddleware,
    userPreferencesApiMiddleware,
    workspacesApiMiddleware,
    queryMiddleware(superAgentNetworkInterface, getQueries, getEntities, {
        backoff: {
            maxAttempts: 1,
            maxDuration: 5000,
            minDuration: 300
        },
        retryableStatusCodes: []
    }),
    routerMiddleware(history)
];
const dynamicMiddleware = () => {
    const getFinalMiddleware = (api) => compose(...injectedMiddlwares.map((m) => {
        if (!m.instance) {
            m.instance = m.middleware(api);
        }
        return m.instance;
    }));
    return (api) => (next) => (action) => getFinalMiddleware(api)(next)(action);
};
export const configuredStore = () => configureStore({
    devTools: process.env.NODE_ENV === "development"
        ? {
            trace: true,
            traceLimit: 25
        }
        : false,
    enhancers: (defaultEnhancers) => defaultEnhancers.concat(createInjectorsEnhancer),
    middleware: (getDefaultMiddleware) => [
        ...getDefaultMiddleware({
            immutableCheck: {
                ignoredPaths: ["auth.oidc.user", "entities"]
            },
            serializableCheck: {
                ignoredActions: [actionTypes.REQUEST_ASYNC, actionTypes.MUTATE_ASYNC, USER_FOUND],
                ignoredPaths: ["auth.oidc.user", "entities"],
                isSerializable: (value) => isPlain(value) || value instanceof Date || value instanceof Blob
            },
            thunk: {
                extraArgument: {
                    getStateValue
                }
            }
        }),
        ...middlewares,
        dynamicMiddleware()
    ],
    preloadedState,
    reducer: createReducer({})
});
